/**
 * Vendor-agnostic organization branding.
 *
 * Branding (logo, theme colors, tagline) lives in the organization document under
 * `customization.branding` (see backend IOrgBranding). The whole app reads it
 * through `resolveBranding()` — there are no per-brand ("isRotary") code paths.
 *
 * `resolveBranding` merges an optional legacy preset (for orgs whose branding has
 * not been seeded into the DB yet) underneath the explicit `customization.branding`
 * object, so explicit data always wins field-by-field and images can fall back to
 * the bundled legacy assets until they are hosted. See `legacyBranding`.
 */
import { legacyBranding } from './legacyBranding';

export interface OrgBrandingColors {
    /** Main brand color (e.g. Rotary royal blue). */
    primary: string;
    /** Darker shade of primary, used for gradients/headers. */
    primaryDark: string;
    /** Secondary/accent color (e.g. Rotary gold). */
    accent: string;
    /** Text color that reads on top of `primary`. */
    textOnPrimary: string;
}

export interface OrgBranding {
    enabled: boolean;
    brandName?: string;
    tagline?: string;
    subtagline?: string;
    logoUrl?: string;
    bannerUrl?: string;
    /**
     * Optional pre-designed full-page cover artwork (e.g. a brand's official
     * print-ready cover). When set, the report cover renders this image
     * full-bleed instead of the data-driven hand-built layout.
     */
    coverImageUrl?: string;
    /** Top-left label on interior report pages — only when `interiorReportChrome` is true. */
    reportHeaderLeft?: string;
    /** Footer club/chapter name on interior report pages — only when `interiorReportChrome` is true. */
    reportFooterLabel?: string;
    colors: OrgBrandingColors;
    /** When true, report PDFs use the branded cover. */
    themedReport: boolean;
    /**
     * When true, interior report pages get org-specific header/footer chrome
     * (top-left label, top-right banner, footer club name + logo). Rotary-only
     * today — set via `customization.branding`, not applied to all themed orgs.
     */
    interiorReportChrome?: boolean;
    /**
     * Report cover style. `'identity'` selects a premium, data-driven cover
     * (brand lockup + student identity block + P3M attribution) for orgs that
     * have no print-ready `coverImageUrl` but want more than the sparse
     * masterbrand vector cover. Opt-in per org via `customization.branding`
     * (RKMM Gurugram today) — every other org keeps its existing cover.
     */
    coverVariant?: 'default' | 'identity';
}

/** Minimal org shape needed to resolve branding (name + customization). */
export type OrgBrandingInput = {
    name?: string | null;
    customization?: Record<string, any> | null;
};

/** Fallback palette when a branding object omits colors. */
export const DEFAULT_BRANDING_COLORS: OrgBrandingColors = {
    primary: '#1560bd',
    primaryDark: '#0d3d82',
    accent: '#007aff',
    textOnPrimary: '#ffffff'
};

/**
 * Resolve the effective branding for an organization, or null when the org has
 * no branding (explicit or legacy). Explicit `customization.branding` wins
 * field-by-field over any legacy preset.
 */
export function resolveBranding(org?: OrgBrandingInput | null): OrgBranding | null {
    if (!org) return null;

    const legacy = legacyBranding(org);
    const explicit = org.customization?.branding as Partial<OrgBranding> | undefined;

    if (explicit && explicit.enabled) {
        const base = legacy;
        return {
            enabled: true,
            brandName: explicit.brandName ?? base?.brandName,
            tagline: explicit.tagline ?? base?.tagline,
            subtagline: explicit.subtagline ?? base?.subtagline,
            // `||` so empty strings fall back to bundled legacy images.
            logoUrl: explicit.logoUrl || base?.logoUrl,
            bannerUrl: explicit.bannerUrl || base?.bannerUrl,
            coverImageUrl: explicit.coverImageUrl || base?.coverImageUrl,
            reportHeaderLeft: explicit.reportHeaderLeft ?? base?.reportHeaderLeft,
            reportFooterLabel: explicit.reportFooterLabel ?? base?.reportFooterLabel,
            interiorReportChrome:
                explicit.interiorReportChrome ?? base?.interiorReportChrome ?? false,
            colors: {
                ...DEFAULT_BRANDING_COLORS,
                ...(base?.colors || {}),
                ...(explicit.colors || {})
            },
            themedReport: explicit.themedReport ?? base?.themedReport ?? false,
            coverVariant: explicit.coverVariant ?? base?.coverVariant
        };
    }

    return legacy;
}

/** Interior PDF header/footer — only orgs with this flag (Rotary customization). */
export function hasInteriorReportChrome(branding?: OrgBranding | null): boolean {
    return Boolean(branding?.interiorReportChrome);
}

/** Convenience: does this org have any active branding? */
export function hasBranding(org?: OrgBrandingInput | null): boolean {
    return !!resolveBranding(org);
}

/** Org fields that may carry a logo (URL and/or cached base64). */
export type OrgLogoInput = OrgBrandingInput & {
    logo?: string | null;
    logoBase64?: string | null;
};

const DEFAULT_LOGO_FRAGMENT = 'organization-avatar.png';

export function isDefaultLogo(url?: string | null): boolean {
    return !url || url.includes(DEFAULT_LOGO_FRAGMENT);
}

/**
 * Best logo for participant screens.
 * Priority: explicit branding logo → hosted org logo (non-default) → base64 only when
 * it is not the generic default avatar placeholder.
 */
export function resolveOrganizationLogoUrl(org?: OrgLogoInput | null): string | undefined {
    if (!org) return undefined;

    const brandingLogo = resolveBranding(org)?.logoUrl;
    if (brandingLogo) return brandingLogo;

    if (org.logo?.startsWith('http') && !isDefaultLogo(org.logo)) {
        return org.logo;
    }

    // Default-avatar sub-orgs often carry a matching generic base64 blob — skip it so
    // inherited master branding (logoUrl above) or a real HTTP logo can show instead.
    if (org.logoBase64?.startsWith('data:') && !isDefaultLogo(org.logo)) {
        return org.logoBase64;
    }

    if (!isDefaultLogo(org.logo)) return org.logo || undefined;

    return undefined;
}
